import { useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Palette, ShieldCheck, X } from "lucide-react";

const WEEK = 7 * 24 * 60 * 60 * 1000;

export function QuickStartTips({ user, kitCount, loading }) {
  const key = `ig_tips_dismissed_${user.id}`;
  const [hidden, setHidden] = useState(() => localStorage.getItem(key) === "1");
  const isNew = user.role !== "admin" && Date.now() - new Date(user.created_at).getTime() < WEEK;
  if (!isNew || hidden || loading) return null;

  const tips = [
    kitCount
      ? { icon: Palette, title: "Keep your kit current", body: `You have ${kitCount} pigment${kitCount === 1 ? "" : "s"} saved. Add new bottles as you buy them so kit-first options stay accurate.`, to: "/kit", cta: "Review kit" }
      : { icon: Palette, title: "Add your pigments first", body: "Ink Genius looks for options from your kit before anything else. Add the shades you own in a minute.", to: "/kit", cta: "Add pigments" },
    { icon: Camera, title: "Photos add context, not answers", body: "Use consented, cropped brow photos in consistent light. They can't confirm undertone, depth, chemistry or readiness.", to: "/consult/new", cta: "Try a consultation" },
    { icon: ShieldCheck, title: "Read the evidence label", body: "“Ingredients documented”, “partially documented” and “not yet verified” show how much is known about each shade.", to: "/library", cta: "Browse library" },
  ];

  const dismiss = () => {
    localStorage.setItem(key, "1");
    setHidden(true);
  };

  return (
    <section className="ig-rise ig-d2 mt-8 rounded-3xl border border-[#E8BDB5] bg-[#FDF2F0] p-6 sm:p-8" data-testid="quick-start-tips" aria-labelledby="qs-title">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono-ig text-[11px] uppercase tracking-[0.3em] text-[#9C6218]">Your first week</p>
          <h2 id="qs-title" className="mt-1 text-2xl font-semibold text-[#2C1810] sm:text-3xl">Three quick start tips</h2>
        </div>
        <button onClick={dismiss} data-testid="quick-start-dismiss" aria-label="Hide quick start tips"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[#6E5549] hover:bg-white/70 hover:text-[#2C1810] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C5A059]">
          <X size={18} />
        </button>
      </div>
      <ol className="mt-6 grid gap-4 md:grid-cols-3">
        {tips.map(({ icon: Icon, title, body, to, cta }, i) => (
          <li key={title} className="flex flex-col rounded-2xl bg-[#FDFBF7] p-5" data-testid={`quick-start-tip-${i + 1}`}>
            <div className="flex items-center gap-3">
              <span className="font-serif text-3xl italic leading-none text-[#C5A059]">{i + 1}</span>
              <Icon size={18} strokeWidth={1.6} className="text-[#2C1810]" />
            </div>
            <p className="mt-3 text-sm font-semibold text-[#2C1810]">{title}</p>
            <p className="mt-1 flex-1 text-sm leading-relaxed text-[#4A3B32]">{body}</p>
            <Link to={to} data-testid={`quick-start-tip-${i + 1}-link`} className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#9C6218] underline-offset-4 hover:underline">{cta} →</Link>
          </li>
        ))}
      </ol>
    </section>
  );
}
